import _ from "lodash";
import React from "react";

// design system
import { Avatar, Tag, Tooltip } from "@toddle-edu/ds-web";

// components
import I18nHOC from "UIComponents/I18nHOC";

// modules
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";

// third party
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

// constants
import { ASSESSMENT } from "Constants/stringConstants";

// scss
import classes from "./AssessmentIcon.scss";

// Utils
import { getInitials as getInitialsForTag } from "Utils";
import { getIsAssessmentTypeConfigurationEnabled } from "Utils/ACLStoreUtils";
import { getTaskTypeAvatarName } from "Utils/EntityFlowUtils";

const EXCLUDED_INITIALS = ["Ed"];

const getInitials = value => {
  const [firstName, lastName = ""] = _.split(value, " ") ?? ["", ""];
  if (!_.isEmpty(firstName) && !_.isEmpty(lastName)) {
    return `${_.first(firstName)}${_.first(lastName)}`;
  }

  return `${_.first(firstName)}`;
};

const AssessmentIcon = props => {
  const {
    t,
    src,
    getAssessmentObj,
    curriculumType,
    contentType = ASSESSMENT,
    assessmentType,
    className,
    size = "medium",
    placement = "bottom",
    curriculumId,
    isVisibleInGlobalSearch = false,
    showTag = false,
    useSingleInitial = false,
    showSpecialCharacters = false,
    iconSize, // passing this prop because size prop is being overridden in pageHeaderv2
    returnOnlyAbbrevationText = false,
    disabled = false,
  } = props;

  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType,
    curriculumId,
    isVisibleInGlobalSearch,
  });

  const { typeLabel, typeLabel2, nonLocalisedLabel, avatarIconColor } =
    typeObj || {};

  const assessmentTypeLabel = t(typeLabel2 || typeLabel);
  const assessmentTypeLabelInitials = getInitials(assessmentTypeLabel);

  const avatarName = getTaskTypeAvatarName({
    type: assessmentType,
    label: assessmentTypeLabel,
    isAssessmentTypeConfigurationEnabled: getIsAssessmentTypeConfigurationEnabled(),
  });

  // EXCLUDED_INITIALS fallback only applies when the avatar uses the label
  // (i.e. it didn't get the legacy "A I"/"W S" override).
  const name =
    avatarName === assessmentTypeLabel &&
    _.includes(EXCLUDED_INITIALS, assessmentTypeLabelInitials) &&
    nonLocalisedLabel
      ? nonLocalisedLabel
      : avatarName;

  const initialsForTag = getInitialsForTag({
    value: _.upperCase(name),
    useSingleInitial,
    showSpecialCharacters,
  });

  if (returnOnlyAbbrevationText) {
    return <span>{initialsForTag}</span>;
  }

  return (
    <Tooltip
      tooltip={assessmentTypeLabel}
      placement={placement}
      showArrow={true}
    >
      <div className={classes.flex}>
        {showTag ? (
          <Tag
            size={size}
            color={avatarIconColor}
            className={className}
            dsVersion={"2.0"}
          >
            {initialsForTag}
          </Tag>
        ) : (
          <Avatar
            src={src}
            shape={"square"}
            shade={"light"}
            size={iconSize || size}
            name={_.upperCase(name)}
            color={avatarIconColor}
            className={className}
            dsVersion={"2.0"}
            disabled={disabled}
          />
        )}
      </div>
    </Tooltip>
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumType =
    ownProps?.curriculumType ?? getCurrentCurriculumProgramType(state);

  return {
    curriculumType,
  };
};

const mapActionCreators = {
  getAssessmentObj,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(AssessmentIcon);
